import { defaultTransform } from "./geometry";
import type { Entity, EntityId } from "./Entity";
import type { EntityComponent, ComponentType } from "./components";
import type { SceneDefinition } from "./SceneDefinition";
import type { EntityDefinitionRegistry } from "../game-content/EntityDefinitionRegistry";

export interface Scene {
  readonly id: string;
  readonly definition: SceneDefinition;
  entities: readonly Entity[];
  /** Baseline entities as instantiated from the definition — used to compute diffs on save. */
  readonly initialEntities: readonly Entity[];
}

/** Instantiates fresh runtime entities from a scene definition (first visit, no save data yet). */
export function instantiateScene(definition: SceneDefinition, registry: EntityDefinitionRegistry): Scene {
  const entities: Entity[] = definition.entities.map((def) => {
    const base = registry.get(def.definitionId);
    if (!base) {
      throw new Error(`No entity definition for "${def.definitionId}" (entity "${def.id}")`);
    }
    const kind = def.kind ?? base.kind;
    const components = mergeComponents(base.components, def.components ?? []);
    return {
      id: def.id,
      kind,
      definitionId: def.definitionId,
      transform: defaultTransform(def.position ?? { x: 0, y: 0 }, def.rotation ?? 0),
      components,
      visible: !def.hidden,
    };
  });
  return { id: definition.id, definition, entities, initialEntities: entities };
}

function mergeComponents(
  base: readonly EntityComponent[],
  overrides: readonly EntityComponent[],
): EntityComponent[] {
  if (overrides.length === 0) return [...base];
  const merged = new Map<ComponentType, EntityComponent>();
  for (const c of base) merged.set(c.type, c);
  for (const c of overrides) merged.set(c.type, c);
  return [...merged.values()];
}

export function findEntity(scene: Scene, id: EntityId): Entity | undefined {
  return scene.entities.find((e) => e.id === id);
}

export function replaceEntity(scene: Scene, updated: Entity): Scene {
  return {
    ...scene,
    entities: scene.entities.map((e) => (e.id === updated.id ? updated : e)),
  };
}

export function removeEntity(scene: Scene, id: EntityId): Scene {
  return { ...scene, entities: scene.entities.filter((e) => e.id !== id) };
}

export function addEntity(scene: Scene, entity: Entity): Scene {
  return { ...scene, entities: [...scene.entities, entity] };
}
